import { Injectable } from '@angular/core';
import { UisAuthenticationErrors } from '../enums/uis-authentication-errors.enum';

declare const $localize;

@Injectable()
export class UisAuthenticationErrorService {
  errorsMap = {
    [UisAuthenticationErrors.NoSuchAttribute]: $localize`The current password is incorrect. Please verify the data and try again.`,
    [UisAuthenticationErrors.ChangedPasswordConstraintViolation]: $localize`The password you have entered matches one of your last 10 passwords. Please choose a new one.`,
    [UisAuthenticationErrors.ConstraintViolation]: $localize`The password you have entered matches one of your last 10 passwords. Please choose a new one.`,
    [UisAuthenticationErrors.NewPasswordHistoryViolation]: $localize`The password you have entered matches one of your last 10 passwords. Please choose a new one.`,
    [UisAuthenticationErrors.LinkExpired]: $localize`Your reset password link is expired. Please request a new link.`,
    [UisAuthenticationErrors.NameOrPasswordIncorrect]: $localize`The email or password you have entered is invalid.`,
    [UisAuthenticationErrors.Unexpected]: $localize`Unexpected error happened. There can be technical issues on our end or related to your system. Please try again shortly.`,
    [UisAuthenticationErrors.UserDisabled]: $localize`User is disabled, please contact support.`,
    [UisAuthenticationErrors.Other]: $localize`The password cannot be saved due to various reasons. These can be technical issues on our end or related to your system. Please try again shortly.`,
    [UisAuthenticationErrors.UserLockedOut]: $localize`You have been locked out of your account, please try to login again after some time.`,
    [UisAuthenticationErrors.PasswordExpired]: $localize`Your password is expired.`,
    [UisAuthenticationErrors.RedirectUriNotValid]: $localize`Unexpected error happened. There can be technical issues on our end or related to your system. Please try again shortly.`,
    [UisAuthenticationErrors.UnauthorizedDomainAccess]: $localize`This user is unauthorized to log in from this domain.`,
  };

  /**
   * Format time duration into human-readable string
   * @param minutes Total minutes
   * @returns Formatted string like "30 minutes", "1 hour", "2 hours"
   */
  private formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    }
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} and ${remainingMinutes} ${remainingMinutes === 1 ? 'minute' : 'minutes'}`;
  }

  /**
   * If no errors are found by the given errorCode
   * return Other error message
   * @param errorCode
   * @param useAlternateMessage
   * @param unlockInMinutes Optional parameter for lockout duration
   */
  getErrorMessage(errorCode: UisAuthenticationErrors, useAlternateMessage?: boolean, unlockInMinutes?: number): string {
    if (errorCode === UisAuthenticationErrors.NameOrPasswordIncorrect && useAlternateMessage) {
      return $localize`The current password is incorrect.`;
    }

    // Handle dynamic lockout message
    if (errorCode === UisAuthenticationErrors.UserLockedOut && unlockInMinutes !== undefined) {
      const duration = this.formatDuration(unlockInMinutes);
      return $localize`Your account has been temporarily locked due to multiple incorrect verification attempts. Please try again after ${duration}.`;
    }

    return this.errorsMap[errorCode] || this.errorsMap[UisAuthenticationErrors.Other];
  }
}
